import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import "@/lib/i18n";
import { I18nGate } from "@/components/i18n/language-switcher";
import { App } from "@/App";
import "@/index.css";

function RootProviders({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  const loadAuth = useAuthStore((s) => s.load);
  const initRouter = useUiStore((s) => s.initRouter);

  useEffect(() => {
    const cleanupRouter = initRouter();
    loadAuth();
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch((e) => {
        console.warn("[sw] registration failed", e);
      });
    }
    return cleanupRouter;
  }, [initRouter, loadAuth]);

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <I18nGate />
        {children}
        <Toaster />
        <SonnerToaster position="top-center" richColors />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <RootProviders>
      <App />
    </RootProviders>
  </StrictMode>
);
